/**
 * cust-tab-product-grid.js
 * Handles tab switching and keeps slider/grid layout state in sync.
 */

const SECTION_SELECTOR = '.cust-tab-product-grid';
const INIT_FLAG = 'custTabProductGridInit';
const DESKTOP_MEDIA_QUERY = '(min-width: 1025px)';
const ITEM_SELECTOR = '.product-item, .cust-tab-product-grid__card';

const updateSingleRowState = (panels) => {
  panels.forEach((panel) => {
    const grid = panel.querySelector('.cust-tab-product-grid__grid');
    if (!grid) return;

    const cols = Number.parseInt(getComputedStyle(grid).getPropertyValue('--tpg-cols'), 10) || 4;
    const itemsCount = grid.querySelectorAll('.product-item').length;
    const isSingleRow = itemsCount > 0 && itemsCount <= cols;

    grid.classList.toggle('is-single-row', isSingleRow);
  });
};

const syncSliderAfterTabSwitch = (activePanel, onComplete) => {
  const complete = typeof onComplete === 'function' ? onComplete : () => {};
  if (!activePanel) {
    complete();
    return;
  }

  const sliders = activePanel.querySelectorAll('scroll-slider-component');
  if (!sliders.length) {
    complete();
    return;
  }

  // Trigger recalculation after panel visibility toggles.
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      sliders.forEach((slider) => {
        if (typeof slider.initScroller === 'function') {
          slider.initScroller();
        }

        if (typeof slider.handleScrollPositionChange === 'function') {
          slider.handleScrollPositionChange();
        }
      });
      complete();
    });
  });
};

const syncPanelColumnHeights = (section) => {
  const panels = section.querySelectorAll('.cust-tab-product-grid__panel');
  const isDesktop = window.matchMedia(DESKTOP_MEDIA_QUERY).matches;

  panels.forEach((panel) => {
    const imageCol = panel.querySelector('.cust-tab-product-grid__image-col');
    const sliderWrap = panel.querySelector('.cust-tab-product-grid__slider-wrap');
    if (!imageCol || !sliderWrap) return;

    imageCol.style.height = '';
    sliderWrap.style.height = '';

    if (!isDesktop) return;

    const unifiedHeight = Math.max(imageCol.offsetHeight, sliderWrap.offsetHeight);
    if (unifiedHeight > 0) {
      imageCol.style.height = `${unifiedHeight}px`;
      sliderWrap.style.height = `${unifiedHeight}px`;
    }
  });
};

const parsePixelValue = (value) => {
  const parsedValue = Number.parseFloat(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const syncPanelInnerOffset = (section) => {
  const panels = section.querySelectorAll('.cust-tab-product-grid__panel');
  const isDesktop = window.matchMedia(DESKTOP_MEDIA_QUERY).matches;

  panels.forEach((panel) => {
    panel.style.setProperty('--ctpg-offset', '0px');

    const isActive = panel.classList.contains('is-active');
    if (!isDesktop || !isActive) return;

    const cols = Number.parseInt(panel.dataset.itemCols, 10) || 0;
    const datasetTotal = Number.parseInt(panel.dataset.itemTotal, 10) || 0;
    if (cols <= 0) return;

    const track = panel.querySelector('.cust-tab-product-grid__grid') || panel.querySelector('.scroll-slider__slider');
    if (!track) return;

    const renderedCount = track.querySelectorAll(ITEM_SELECTOR).length;
    const total = renderedCount > 0 ? Math.min(datasetTotal || renderedCount, renderedCount) : datasetTotal;
    if (total < 0 || cols <= total) return;

    const missing = cols - total;
    const firstItem = track.querySelector(ITEM_SELECTOR);
    const fallbackItemWidth = cols > 0 ? track.clientWidth / cols : 0;
    const itemWidth = firstItem ? firstItem.getBoundingClientRect().width : fallbackItemWidth;
    const trackStyles = window.getComputedStyle(track);
    const gap = parsePixelValue(trackStyles.columnGap || trackStyles.gap);
    const missingWidth = missing * (itemWidth + gap);
    const offset = missingWidth / 2;

    if (offset > 0) {
      panel.style.setProperty('--ctpg-offset', `${offset}px`);
    }
  });
};

const initSection = (section) => {
  if (section.dataset[INIT_FLAG] === 'true') return;

  const tabs = Array.from(section.querySelectorAll('.cust-tab-product-grid__tab'));
  const panels = Array.from(section.querySelectorAll('.cust-tab-product-grid__panel'));
  if (!tabs.length || !panels.length) return;

  section.dataset[INIT_FLAG] = 'true';

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const targetIndex = tab.dataset.tabIndex;
      let activePanel = null;

      tabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', String(isActive));
      });

      panels.forEach((panel) => {
        const isActive = panel.dataset.panelIndex === targetIndex;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', String(!isActive));
        if (isActive) activePanel = panel;
      });

      updateSingleRowState(panels);
      syncSliderAfterTabSwitch(activePanel, () => {
        syncPanelInnerOffset(section);
      });
      syncPanelColumnHeights(section);
    });
  });

  const onResize = () => {
    updateSingleRowState(panels);
    syncPanelColumnHeights(section);
    syncPanelInnerOffset(section);
  };
  section._custTabProductGridOnResize = onResize;
  window.addEventListener('resize', onResize);
  updateSingleRowState(panels);
  syncPanelColumnHeights(section);
  syncPanelInnerOffset(section);

  section.querySelectorAll('.cust-tab-product-grid__image').forEach((image) => {
    if (image.complete) return;

    image.addEventListener('load', () => syncPanelColumnHeights(section), { once: true });
  });
};

const destroySection = (section) => {
  if (!section) return;

  const onResize = section._custTabProductGridOnResize;
  if (onResize) {
    window.removeEventListener('resize', onResize);
    delete section._custTabProductGridOnResize;
  }

  delete section.dataset[INIT_FLAG];
};

const initAllSections = () => {
  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
};

document.addEventListener('DOMContentLoaded', initAllSections);
document.addEventListener('shopify:section:load', initAllSections);
document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.closest(SECTION_SELECTOR);
  destroySection(section);
});
